import React, { createContext, useContext, useMemo, useCallback } from 'react';

/**
 * UI Context: Transient UI state (not persisted to sessionStorage).
 * Manages: modals, loading states, error messages, etc.
 */

const UIContext = createContext();

export const UIProvider = ({ children }) => {
  const [isExitCardActive, setIsExitCardActiveState] = React.useState(false);
  const [isCartActive, setIsCartActiveState] = React.useState(false);
  const [showServicesPopup, setShowServicesPopupState] = React.useState(false);
  const [isDiscardPopupActive, setIsDiscardPopupActiveState] = React.useState(false);
  const [isLoadingMainActive, setIsLoadingMainActiveState] = React.useState(true);
  const [error, setErrorState] = React.useState({});

  const setIsExitCardActive = useCallback((active) => {
    setIsExitCardActiveState(active);
  }, []);

  const setIsCartActive = useCallback((active) => {
    setIsCartActiveState(active);
  }, []);

  const setShowServicesPopup = useCallback((show) => {
    setShowServicesPopupState(show);
  }, []);

  const setIsDiscardPopupActive = useCallback((active) => {
    setIsDiscardPopupActiveState(active);
  }, []);

  const setIsLoadingMainActive = useCallback((active) => {
    setIsLoadingMainActiveState(active);
  }, []);

  const setError = useCallback((err) => {
    setErrorState(err);
  }, []);

  // Helper to clear error
  const clearError = useCallback(() => {
    setErrorState({});
  }, []);

  // Helper to set error message
  const setErrorMessage = useCallback((message) => {
    setErrorState({ error: message });
  }, []);

  const value = useMemo(
    () => ({
      isExitCardActive,
      setIsExitCardActive,
      isCartActive,
      setIsCartActive,
      showServicesPopup,
      setShowServicesPopup,
      isDiscardPopupActive,
      setIsDiscardPopupActive,
      isLoadingMainActive,
      setIsLoadingMainActive,
      error,
      setError,
      clearError,
      setErrorMessage,
    }),
    [
      isExitCardActive,
      setIsExitCardActive,
      isCartActive,
      setIsCartActive,
      showServicesPopup,
      setShowServicesPopup,
      isDiscardPopupActive,
      setIsDiscardPopupActive,
      isLoadingMainActive,
      setIsLoadingMainActive,
      error,
      setError,
      clearError,
      setErrorMessage,
    ]
  );

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
};

export default UIContext;
